/** @jsxImportSource @emotion/react */
import React, { memo, useCallback, useMemo, useRef, useState } from 'react'
import {
  CHOICE_ID_CANCEL,
  CHOICE_ID_NO,
  CHOICE_ID_OK,
  OK_CANCEL_PROMPT_CHOICES,
  OK_PROMPT_CHOICES,
  YES_NO_PROMPT_CHOICES
} from './constants'
import { withRouteChangePrompt } from './routeChangeHoc'
import {
  ModalPromptContextType,
  ModalPromptCustomReturnType,
  ModalPromptGeneratorReturnType,
  ModalPromptOverridablePropsType,
  ModalPromptPropsType,
  RouteChangeHocExtraPropsType
} from './types'
import ModalPromptContext from './Context'
import ModalPrompt from './Component'

type PromptPromiseResolveType = (choiceId: string) => void

type AppModalPromptGeneratorPropsType = {
  children: React.ReactNode
}

export const AppModalPromptGenerator: React.FC<
  AppModalPromptGeneratorPropsType & RouteChangeHocExtraPropsType
> = ({
  children,
  invokeRouteChangeRequest,
  onModalContext,
  registerRouteChangePrompt
}) => {
  const [activePrompt, setActivePrompt] =
    useState<ModalPromptOverridablePropsType>({})
  const [dismissed, setDismissed] = useState<boolean>(true)
  const promptResolveRef = useRef<PromptPromiseResolveType>()

  // standard choice always resolves/dismisses, you can override to prevent a resolve/dismiss
  const handleChoice = useCallback((): boolean => true, [])

  const handleDismiss = useCallback((choiceId?: string): void => {
    if (promptResolveRef.current && choiceId) {
      promptResolveRef.current(choiceId)
    }
    setDismissed(true)
  }, [])

  const modalPromptContext = useMemo((): ModalPromptContextType => {
    const modalPromptContext = {
      invokeRouteChangeRequest,
      registerRouteChangePrompt,
      // function exposed on context for generating a custom prompt
      promptCustom: (
        props: ModalPromptPropsType
      ): ModalPromptCustomReturnType => ({
        // consumer can call this to dismiss the modal
        dismiss: (choiceId?: string) => {
          handleDismiss(choiceId)
        },
        // consumer can call this to enable/disable choice buttons on the modal
        enableChoice: (choiceId: string, enabled: boolean) => {
          setActivePrompt(props => {
            if (!props.disabledChoices) {
              props.disabledChoices = new Set()
            }
            if (enabled) {
              props.disabledChoices.delete(choiceId)
            } else {
              props.disabledChoices.add(choiceId)
            }
            return {
              ...props,
              disabledChoices: new Set(props.disabledChoices)
            }
          })
        },
        // consumer can all this to await the result of the prompt
        result: new Promise((resolve: PromptPromiseResolveType): void => {
          promptResolveRef.current = resolve
          setActivePrompt(props)
          setDismissed(false)
        })
      }),
      // function exposed on context for generating an ok prompt
      promptWithOk: (
        overrides: ModalPromptOverridablePropsType
      ): ModalPromptGeneratorReturnType =>
        new Promise((resolve: PromptPromiseResolveType): void => {
          promptResolveRef.current = resolve
          setActivePrompt({
            choices: OK_PROMPT_CHOICES,
            defaultChoiceId: CHOICE_ID_OK,
            ...overrides
          })
          setDismissed(false)
        }),
      // function exposed on context for generating an ok/cancel prompt
      promptWithOkCancel: (
        overrides: ModalPromptOverridablePropsType
      ): ModalPromptGeneratorReturnType =>
        new Promise((resolve: PromptPromiseResolveType): void => {
          promptResolveRef.current = resolve
          setActivePrompt({
            choices: OK_CANCEL_PROMPT_CHOICES,
            defaultChoiceId: CHOICE_ID_CANCEL,
            ...overrides
          })
          setDismissed(false)
        }),
      // function exposed on context for generating a yes/no prompt prompt
      promptWithYesNo: (
        overrides: ModalPromptOverridablePropsType
      ): ModalPromptGeneratorReturnType =>
        new Promise((resolve: PromptPromiseResolveType): void => {
          promptResolveRef.current = resolve
          setActivePrompt({
            choices: YES_NO_PROMPT_CHOICES,
            defaultChoiceId: CHOICE_ID_NO,
            ...overrides
          })
          setDismissed(false)
        })
    }
    if (onModalContext) {
      onModalContext(modalPromptContext)
    }
    return modalPromptContext
  }, [
    handleDismiss,
    invokeRouteChangeRequest,
    onModalContext,
    registerRouteChangePrompt,
    setActivePrompt,
    setDismissed
  ])

  return (
    <ModalPromptContext.Provider value={modalPromptContext}>
      {children}
      <ModalPrompt
        onChoice={handleChoice}
        onDismiss={handleDismiss}
        {...activePrompt}
        showPrompt={!dismissed && Boolean(activePrompt.choices)}
      />
    </ModalPromptContext.Provider>
  )
}

export default memo(
  withRouteChangePrompt<AppModalPromptGeneratorPropsType>(
    AppModalPromptGenerator
  ) as React.ComponentType<AppModalPromptGeneratorPropsType>
)
